/**
 * STYLING: Plain Tailwind ONLY!
 * Shared text tool page - parameterized by toolCode from route.
 */

import { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TextToolHeader } from './TextToolHeader';
import { TextToolInput } from './TextToolInput';
import { TextToolSettings } from './TextToolSettings';
import { TextToolActions } from './TextToolActions';
import { TextToolVariantSelect } from './TextToolVariantSelect';
import { TranslateLanguageSelector } from './TranslateLanguageSelector';
import { VisionImagePicker } from '@/components/v2/shared/VisionImagePicker';
import { BrandPicker } from '@/components/v2/shared/BrandPicker';
import { EID } from '@/lib/element-ids.registry';
import {
  fetchToolConfig,
  generateText,
  refineText,
  type RefineIntent,
  type TextGenerateResponse,
} from './textGenerationApi';
import { TEXT_TOOLS } from './textToolsConfig';

export function TextToolPage() {
  const { toolCode } = useParams<{ toolCode: string }>();
  const toolDef = TEXT_TOOLS.find((t) => t.code === toolCode);

  const { data: config, isLoading } = useQuery({
    queryKey: ['text-tool-config', toolCode],
    queryFn: () => fetchToolConfig(toolCode!),
    enabled: !!toolCode,
    staleTime: 5 * 60 * 1000,
  });

  const [prompt, setPrompt] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [model, setModel] = useState('gemini-2.5-flash');
  const [temperature, setTemperature] = useState(0.7);
  const [googleSearch, setGoogleSearch] = useState(false);
  const [outputFormat, setOutputFormat] = useState('plain');
  const [writingStyle, setWritingStyle] = useState('balanced');
  const [naturalStyle, setNaturalStyle] = useState(true);
  const [targetLanguage, setTargetLanguage] = useState('');
  // Welle 1 Modifier
  const [textStyle, setTextStyle] = useState('');
  const [lengthBucket, setLengthBucket] = useState('');
  const [readingLevel, setReadingLevel] = useState('');
  const [goal, setGoal] = useState('standard');
  // Welle 3 Multi-Modal
  const [imageKey, setImageKey] = useState<string | null>(null);
  // Welle 4 Brand-Voice
  const [brandId, setBrandId] = useState<string | null>(null);
  // Welle 5 Multi-Variant + Refine
  const [nVariants, setNVariants] = useState(1);
  const [refining, setRefining] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TextGenerateResponse | null>(null);

  if (!toolCode || !toolDef) return <Navigate to="/v2/tools/text" replace />;

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await generateText({
        tool_code: toolCode, prompt,
        system_prompt: systemPrompt || undefined, model,
        temperature,
        use_cache: true, json_mode: outputFormat === 'json',
        google_search: googleSearch, output_format: outputFormat,
        writing_style: writingStyle, natural_style: naturalStyle,
        target_language: targetLanguage || undefined,
        text_style: textStyle || undefined,
        length_bucket: lengthBucket || undefined,
        reading_level: readingLevel || undefined,
        goal: goal !== 'standard' ? goal : undefined,
        image_key: imageKey || undefined,
        brand_id: brandId || undefined,
        n_variants: nVariants,
      });
      setResult(res);
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefine = async (intent: RefineIntent, sourceContent: string) => {
    if (!sourceContent.trim()) return;
    setRefining(true);
    setError(null);
    try {
      setResult(await refineText({
        tool_code: toolCode,
        previous_output: sourceContent,
        refine_intent: intent,
        model,
      }));
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setRefining(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div id={`text-tool-page-${toolCode}`} className="container mx-auto p-6 max-w-4xl space-y-6">
      <TextToolHeader
        toolCode={toolCode}
        name={config?.name || toolDef.name} description={config?.description || toolDef.description}
        tier={config?.tier || toolDef.tier} baseCredits={config?.base_credits || 0}
        multipliers={config?.model_multipliers || {}} selectedModel={model}
        promptLength={prompt.length} inputSizeTiers={config?.input_size_tiers}
        googleSearch={googleSearch}
      />
      <TextToolInput
        toolCode={toolCode}
        prompt={prompt} onPromptChange={setPrompt}
        systemPrompt={systemPrompt || config?.system_prompt || ''} onSystemPromptChange={setSystemPrompt}
        placeholder={toolDef.placeholder} canEditSystem={true}
      />
      {toolCode === 'text_translate' && (
        <TranslateLanguageSelector value={targetLanguage} onChange={setTargetLanguage} />
      )}
      {config?.supports_vision && (
        <VisionImagePicker
          imageKey={imageKey}
          onChange={setImageKey}
          surchargeHint="+5 CR"
        />
      )}
      <BrandPicker
        id={EID['text-tool-brand-dropdown']}
        value={brandId}
        onChange={setBrandId}
      />
      <TextToolSettings
        toolCode={toolCode}
        model={model} onModelChange={setModel} temperature={temperature} onTemperatureChange={setTemperature}
        googleSearch={googleSearch} onGoogleSearchChange={setGoogleSearch}
        outputFormat={outputFormat} onOutputFormatChange={setOutputFormat}
        writingStyle={writingStyle} onWritingStyleChange={setWritingStyle}
        naturalStyle={naturalStyle} onNaturalStyleChange={setNaturalStyle}
        textStyle={textStyle} onTextStyleChange={setTextStyle}
        lengthBucket={lengthBucket} onLengthBucketChange={setLengthBucket}
        readingLevel={readingLevel} onReadingLevelChange={setReadingLevel}
        goal={goal} onGoalChange={setGoal}
      />
      <TextToolVariantSelect value={nVariants} onChange={setNVariants} />
      <TextToolActions
        toolCode={toolCode}
        loading={loading}
        disabled={loading || !prompt.trim() || (toolCode === 'text_translate' && !targetLanguage)}
        error={error}
        result={result}
        onGenerate={handleGenerate}
        onRefine={handleRefine}
        refining={refining}
      />
    </div>
  );
}
